import type {
  AfriexCurrency,
  AfriexTransactionChannel,
  AfriexTransactionProcessor,
  AfriexTransactionType,
  CurrencyParams,
  CurrencySetting,
  ProcessorWithEmpty,
} from '@/types';
import type {CurrencyLimitSetting, CurrencyPermissions} from '@/types/Currency';

// State interface
export interface CurrencyFormState {
  params: Partial<CurrencyParams>;
  business: CurrencySetting;
  // Retail limits. Only each type's `limits` sibling is served by the detail
  // endpoint, so this subtree never carries the retail processor slots (those
  // are edited from Processors -> Corridors).
  user: CurrencySetting;
  // The corridor kill switches. Only an explicit `false` blocks, so an absent
  // field here means the corridor is open.
  permissions: CurrencyPermissions;
  isLoading: boolean;
  errors: Record<string, string>;
}

// Action types
export type CurrencyFormAction =
  | {type: 'INITIALIZE_FORM'; payload: AfriexCurrency}
  | {
      type: 'UPDATE_PARAM';
      payload: {key: keyof CurrencyParams; value: string | number};
    }
  | {
      type: 'UPDATE_BUSINESS_PROCESSOR';
      payload: {
        transactionType: AfriexTransactionType;
        channel: AfriexTransactionChannel;
        processor: AfriexTransactionProcessor;
      };
    }
  | {
      type: 'REMOVE_BUSINESS_PROCESSOR';
      payload: {
        transactionType: AfriexTransactionType;
        channel: AfriexTransactionChannel;
      };
    }
  | {
      type: 'SET_CURRENCY_PERMISSION';
      payload: {permission: keyof CurrencyPermissions; allowed: boolean};
    }
  | {
      type: 'SET_USER_LIMITS';
      payload: {
        transactionType: AfriexTransactionType;
        limits: CurrencyLimitSetting;
      };
    }
  | {type: 'SET_LOADING'; payload: boolean}
  | {type: 'SET_ERROR'; payload: {field: string; message: string}}
  | {type: 'CLEAR_ERROR'; payload: string}
  | {type: 'CLEAR_ALL_ERRORS'};

// Initial state
export const initialCurrencyFormState: CurrencyFormState = {
  params: {},
  business: {},
  user: {},
  permissions: {},
  isLoading: false,
  errors: {},
};

// Reducer function
export const currencyFormReducer = (
  state: CurrencyFormState,
  action: CurrencyFormAction,
): CurrencyFormState => {
  switch (action.type) {
    case 'INITIALIZE_FORM':
      return {
        ...state,
        params: action.payload.params ?? {},
        business: action.payload.business ?? {},
        user: action.payload.user ?? {},
        permissions: action.payload.permissions ?? {},
        errors: {},
      };

    case 'UPDATE_PARAM': {
      const newState = {
        ...state,
        params: {
          ...state.params,
          [action.payload.key]: action.payload.value,
        },
        errors: {
          ...state.errors,
          [action.payload.key]: '', // Clear error for this field
        },
      };
      return newState;
    }

    case 'UPDATE_BUSINESS_PROCESSOR': {
      const {transactionType, channel, processor} = action.payload;
      return {
        ...state,
        business: {
          ...state.business,
          [transactionType]: {
            ...state.business[transactionType],
            [channel]: {
              ...state.business[transactionType]?.[channel],
              currentProcessor: processor,
            },
          },
        },
        errors: {
          ...state.errors,
          [`${transactionType}_${channel}`]: '', // Clear error for this field
        },
      };
    }

    case 'REMOVE_BUSINESS_PROCESSOR': {
      const {transactionType, channel} = action.payload;
      return {
        ...state,
        business: {
          ...state.business,
          [transactionType]: {
            ...state.business[transactionType],
            [channel]: {
              ...state.business[transactionType]?.[channel],
              currentProcessor: '',
            },
          },
        },
        errors: {
          ...state.errors,
          [`${transactionType}_${channel}`]: '', // Clear error for this field
        },
      };
    }

    // Merges rather than replacing the limits object, so editing one field
    // cannot blank a sibling the form is not currently showing.
    case 'SET_CURRENCY_PERMISSION': {
      const {permission, allowed} = action.payload;
      return {
        ...state,
        permissions: {...state.permissions, [permission]: allowed},
      };
    }

    case 'SET_USER_LIMITS': {
      const {transactionType, limits} = action.payload;
      return {
        ...state,
        user: {
          ...state.user,
          [transactionType]: {
            ...state.user[transactionType],
            limits: {
              ...state.user[transactionType]?.limits,
              ...limits,
            },
          },
        },
      };
    }

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.field]: action.payload.message,
        },
      };

    case 'CLEAR_ERROR': {
      const {[action.payload]: _, ...remainingErrors} = state.errors;
      return {
        ...state,
        errors: remainingErrors,
      };
    }

    case 'CLEAR_ALL_ERRORS':
      return {
        ...state,
        errors: {},
      };

    default:
      return state;
  }
};

// Helper function to get current processor for a transaction type and channel
export const getCurrentProcessor = (
  business: CurrencySetting,
  transactionType: AfriexTransactionType,
  channel: AfriexTransactionChannel,
): ProcessorWithEmpty | undefined => {
  return business[transactionType]?.[channel]?.currentProcessor;
};

// Helper function to check if form is valid
export const isFormValid = (
  state: CurrencyFormState,
  currencyCode?: string,
): boolean => {
  const isUSD = currencyCode === 'USD';
  const hasNoErrors = Object.values(state.errors).every((error) => !error);

  // For USD currency, don't require referralPayoutRate
  // For other currencies, require referralPayoutRate to be set and greater than 0
  const hasRequiredParams = isUSD
    ? true
    : !!(
        state.params.referralPayoutRate && state.params.referralPayoutRate > 0
      );

  return hasRequiredParams && hasNoErrors;
};

// Helper function to prepare data for API submission
export const prepareSubmissionData = (
  state: CurrencyFormState,
  originalCurrency: AfriexCurrency,
): Partial<AfriexCurrency> => {
  return {
    params: {...originalCurrency.params, ...state.params},
    business: {...originalCurrency.business, ...state.business},
    user: {...originalCurrency.user, ...state.user},
    permissions: {...originalCurrency.permissions, ...state.permissions},
  };
};
